import React, { useEffect, useState } from 'react';

import Icon from 'views/site/Icon.js';

import Button from 'views/site/Button.js';
import { submit } from 'files/QuoteRequest.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * QuoteRequestModal
 *
 * Ported from the storefront's consultation modal and given the quote to
 * carry. Two changes beyond the obvious:
 *
 * The name is collected as two fields, because the platform stores a lead as
 * first and last name and splitting a single field on whitespace gets people's
 * names wrong for no good reason.
 *
 * The list being requested is shown inside the form. Someone who built a quote
 * across several pages over several minutes should not have to trust their
 * memory at the one moment they are handing over a phone number.
 */

const QuoteRequestModal = ({ dealership, items, onClose, onSubmitted, open, quiz, utils }) => {

    const [address, setAddress] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const list = items || [];

    // Reset a moment after closing, so the next open is clean but the form
    // does not visibly empty itself during the close animation.
    useEffect(() => {
        if(open) {
            return;
        }
        let timeout = setTimeout(() => {
            setAddress('');
            setEmail('');
            setError(null);
            setFirstName('');
            setLastName('');
            setPhone('');
            setSubmitted(false);
        }, 350);
        return () => clearTimeout(timeout);
    }, [open]);

    useEffect(() => {
        if(!open) {
            return;
        }
        const onKeyDown = evt => evt.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const onSubmitClick = async () => {

        if(!firstName.trim() || !lastName.trim()) {
            setError('Please enter your first and last name.');
            return;
        }
        if(Utils.validateEmail(email) === false) {
            setError('Please enter a valid email address.');
            return;
        }
        if(phone.replace(/[^0-9]/g, '').length < 10) {
            setError('Please enter a valid phone number.');
            return;
        }
        if(!address.trim()) {
            setError('Please enter your home address so we know where to visit.');
            return;
        }

        setError(null);
        setSubmitting(true);

        try {
            await submit(utils, {
                customer: { address, email, firstName, lastName, phone },
                dealership: dealership,
                items: list,
                quiz: quiz
            });
            setSubmitted(true);
            if(onSubmitted) {
                onSubmitted();
            }
        } catch(e) {
            setError(e.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }

    const getFieldLabel = label => (
        <span style={{
            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 13, fontWeight: 600 }),
            display: 'block',
            marginBottom: 6
        }}>{label}</span>
    );

    const getField = (label, value, onChange, props = {}) => (
        <label style={{ display: 'block' }}>
            {getFieldLabel(label)}
            {props.multiline === true ? (
                <textarea
                onChange={evt => onChange(evt.target.value)}
                placeholder={props.placeholder}
                rows={2}
                style={{
                    ...Theme.styles.input(),
                    borderRadius: 12,
                    lineHeight: 1.5,
                    minHeight: 56,
                    resize: 'vertical'
                }}
                value={value} />
            ) : (
                <input
                onChange={evt => onChange(evt.target.value)}
                placeholder={props.placeholder}
                style={{ ...Theme.styles.input(), borderRadius: 12 }}
                type={props.type || 'text'}
                value={value} />
            )}
        </label>
    );

    /*
     * The quote, as an invoice.
     *
     * The previous line — "requesting a quote on 6 products" — was true and
     * unhelpful: six is the number of DISTINCT products, but thirty-three
     * devices are being asked for, and the number a reader takes away should
     * be the one that describes what is actually going in their house. So the
     * count moves to a totals row that states both, and the list gets column
     * headings and a rule above the total, which is the shape people already
     * know how to read.
     */
    const getSummary = () => {

        if(list.length === 0) {
            return null;
        }

        let units = list.reduce((total, item) => total + item.quantity, 0);

        const row = (left, right, options = {}) => (
            <div
            key={options.key}
            style={{
                alignItems: 'baseline',
                borderTop: options.ruled === true ? `1px solid ${Theme.colors.border()}` : 'none',
                display: 'flex',
                gap: 16,
                justifyContent: 'space-between',
                padding: options.tight === true ? '8px 0' : '10px 0',
                ...options.style
            }}>
                {left}
                {right}
            </div>
        );

        return (
            <div style={{
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 14,
                marginBottom: 24,
                overflow: 'hidden'
            }}>
                {/* column headings */}
                <div style={{
                    alignItems: 'baseline',
                    backgroundColor: Theme.colors.offWhite,
                    borderBottom: `1px solid ${Theme.colors.border()}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 18px'
                }}>
                    <span style={Theme.textStyles.caption({
                        color: Theme.colors.subText(),
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                    })}>{'Item'}</span>
                    <span style={Theme.textStyles.caption({
                        color: Theme.colors.subText(),
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                    })}>{'Qty'}</span>
                </div>

                <div style={{ padding: '4px 18px 0' }}>
                    {list.map((item, index) => row(
                        <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14 })}>
                            {item.title}
                        </span>,
                        <span style={{
                            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14, fontWeight: 700 }),
                            fontVariantNumeric: 'tabular-nums'
                        }}>{item.quantity}</span>,
                        { key: item.handle, tight: true, style: { borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}` } }
                    ))}
                </div>

                {/* totals — both numbers stated, so neither can mislead */}
                <div style={{ padding: '0 18px 12px' }}>
                    {row(
                        <span style={Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: 13 })}>
                            {`${list.length} ${list.length === 1 ? 'product' : 'products'}`}
                        </span>,
                        <span style={{
                            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14, fontWeight: 700 }),
                            fontVariantNumeric: 'tabular-nums'
                        }}>{`${units} ${units === 1 ? 'device' : 'devices'}`}</span>,
                        { ruled: true }
                    )}
                </div>
            </div>
        )
    }

    const getSuccess = () => (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: '20px 0',
            textAlign: 'center'
        }}>
            <div style={{
                alignItems: 'center',
                backgroundColor: 'rgba(47,168,79,0.15)',
                borderRadius: 32,
                display: 'flex',
                height: 64,
                justifyContent: 'center',
                width: 64
            }}>
                <Icon name={'check-lg'} size={26} color={Theme.colors.success} />
            </div>
            <span style={Theme.textStyles.heading()}>{'We’ll be in touch'}</span>
            <span style={Theme.textStyles.body({ textAlign: 'center' })}>
                {`Thanks, ${firstName.trim()}. ${dealership?.name || 'Your local dealer'} will reach out within one business day to arrange a time, walk your home with you, and go through what it needs.`}
            </span>
            <Button
            label={'Done'}
            onClick={onClose}
            style={{ marginTop: 8, width: '100%' }}
            type={'primary'} />
        </div>
    );

    const getForm = () => (
        <>
            <span style={{
                ...Theme.textStyles.label(),
                display: 'block',
                marginBottom: 10
            }}>{'No obligation'}</span>
            <span style={{
                ...Theme.textStyles.heading(),
                display: 'block',
                marginBottom: 10
            }}>{'Let’s protect your home'}</span>
            <span style={{
                ...Theme.textStyles.body({ fontSize: 14 }),
                display: 'block',
                marginBottom: 24
            }}>{`Share a few details and ${dealership?.name || 'your local dealer'} will arrange a time to walk your home with you and go through the network you have put together.`}</span>

            {getSummary()}

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14
            }}>
                <div style={{
                    display: 'grid',
                    gap: 14,
                    gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr'
                }}>
                    {getField('First Name', firstName, setFirstName, { placeholder: 'Jane' })}
                    {getField('Last Name', lastName, setLastName, { placeholder: 'Doe' })}
                </div>
                {getField('Email Address', email, setEmail, { placeholder: 'jane@example.com', type: 'email' })}
                {getField('Phone Number', phone, setPhone, { placeholder: '(555) 123-4567', type: 'tel' })}
                {getField('Home Address', address, setAddress, { multiline: true, placeholder: '123 Main St, City, State ZIP' })}
            </div>

            {error && (
                <span style={{
                    ...Theme.textStyles.caption({ color: Theme.colors.error }),
                    display: 'block',
                    marginTop: 14
                }}>{error}</span>
            )}

            <Button
            icon={'send-fill'}
            label={'Request My In-Home Visit'}
            loading={submitting}
            onClick={onSubmitClick}
            style={{ marginTop: 24, width: '100%' }}
            type={'primary'} />
        </>
    );

    return (
        <>
            <div
            className={`site-overlay ${open ? 'active' : ''}`}
            onClick={onClose} />

            <div
            className={'site'}
            style={{
                alignItems: 'center',
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                padding: 20,
                pointerEvents: open ? 'all' : 'none',
                position: 'fixed',
                right: 0,
                top: 0,
                zIndex: 6500
            }}>
                <div style={{
                    backgroundColor: Theme.colors.white,
                    borderRadius: 24,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    maxHeight: '90vh',
                    maxWidth: 520,
                    opacity: open ? 1 : 0,
                    overflowY: 'auto',
                    position: 'relative',
                    transform: open ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                    width: '100%'
                }}>
                    <div
                    onClick={onClose}
                    style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.offWhite,
                        borderRadius: 18,
                        cursor: 'pointer',
                        display: 'flex',
                        height: 36,
                        justifyContent: 'center',
                        position: 'absolute',
                        right: 20,
                        top: 20,
                        width: 36,
                        zIndex: 1
                    }}>
                        <Icon name={'x-lg'} size={14} color={Theme.colors.darkGrey} />
                    </div>

                    <div style={{ padding: Utils.isMobile() ? '36px 24px' : '44px 40px' }}>
                        {submitted ? getSuccess() : getForm()}
                    </div>
                </div>
            </div>
        </>
    )
}

export default QuoteRequestModal;
